import { Outlet, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ClipboardList, LayoutGrid, Coffee, ScanLine, History, Settings, Volume2, VolumeX } from 'lucide-react';

import { ConnectionPill } from '@/components/layout/ConnectionPill';
import { LanguageMenu } from '@/components/layout/LanguageMenu';
import { Brandmark } from '@/components/layout/Brandmark';
import { PausedBanner } from '@/components/shop/PausedBanner';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';
import { useWebSocket } from '@/hooks/useWebSocket';
import { unlockAudio } from '@/lib/audio';
import { cn } from '@/lib/utils';
import { useEffect } from 'react';

interface NavItem {
  to: string;
  labelKey: string;
  icon: typeof ClipboardList;
}

const navItems: NavItem[] = [
  { to: '/orders', labelKey: 'nav.orders', icon: ClipboardList },
  { to: '/tables', labelKey: 'nav.tables', icon: LayoutGrid },
  { to: '/menu', labelKey: 'nav.menu', icon: Coffee },
  { to: '/pickup', labelKey: 'nav.pickup', icon: ScanLine },
  { to: '/history', labelKey: 'nav.history', icon: History },
  { to: '/settings', labelKey: 'nav.settings', icon: Settings },
];

export function Shell() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const soundOn = useUIStore((s) => s.soundOn);
  const setSoundOn = useUIStore((s) => s.setSoundOn);
  const status = useWebSocket(Boolean(user));

  useEffect(() => {
    const onFirstGesture = () => {
      unlockAudio();
      window.removeEventListener('pointerdown', onFirstGesture);
    };
    window.addEventListener('pointerdown', onFirstGesture, { once: true });
    return () => window.removeEventListener('pointerdown', onFirstGesture);
  }, []);

  return (
    <div className="flex h-full flex-col md:flex-row">
      {/* Sidebar (tablet+) */}
      <aside className="relative hidden border-r border-border bg-background md:flex md:w-[260px] md:flex-col">
        <NavLink
          to="/orders"
          className="flex items-center gap-3 px-6 pb-3 pt-7 transition-opacity hover:opacity-80"
          aria-label="Hoopla — back to orders"
        >
          <Brandmark className="text-primary" />
          <div className="leading-tight">
            <div className="font-mono text-2xl text-foreground">Hoopla</div>
            <div className="label-tracked">Cassa</div>
          </div>
        </NavLink>

        {user && (
          <div className="mx-5 mt-2 rounded-md border border-border bg-muted/40 p-3 text-sm">
            <div className="label-tracked text-[10px]">Cashier</div>
            <div className="mt-0.5 truncate font-medium text-foreground">{user.name}</div>
            <div className="mt-0.5 truncate text-xs text-muted-foreground">
              Shop #{user.shopId} · {user.role.toLowerCase()}
            </div>
          </div>
        )}

        <nav className="mt-5 flex-1 space-y-1 px-3">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'group relative flex items-center gap-3 rounded-md px-3 py-2.5 text-[15px] font-medium transition-all',
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-[0_2px_0_0_hsl(var(--primary-deep))]'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon className="h-[18px] w-[18px]" strokeWidth={isActive ? 2.4 : 2} />
                  <span>{t(item.labelKey)}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="px-6 pb-6 pt-3">
          <div className="dotted-rule mb-3" />
          <p className="text-xs text-muted-foreground">
            “Take time to smell the coffee.”
          </p>
        </div>
      </aside>

      {/* Main */}
      <div className="flex min-h-0 flex-1 flex-col">
        <header className="flex h-16 items-center justify-between gap-3 border-b border-border bg-background/80 px-4 backdrop-blur-md">
          <div className="flex items-center gap-3">
            <ConnectionPill status={status} />
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost"
              size="icon"
              aria-label="toggle sound"
              onClick={() => setSoundOn(!soundOn)}
            >
              {soundOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            </Button>
            <LanguageMenu />
          </div>
        </header>

        <PausedBanner />

        <main className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden bg-page">
          <Outlet />
        </main>

        {/* Bottom nav (mobile) */}
        <nav className="grid grid-cols-6 border-t border-border bg-background/95 backdrop-blur md:hidden">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium tracking-wide',
                  isActive ? 'text-primary' : 'text-muted-foreground',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon className="h-5 w-5" strokeWidth={isActive ? 2.4 : 1.8} />
                  {t(item.labelKey)}
                </>
              )}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
